import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { X, Plus, ArrowCounterClockwise, BellRinging } from "@phosphor-icons/react";

/** Editable word list rendered as chips with an add box and a reset-to-defaults. */
export function WordsEditor({ label, hint, words, defaults, onChange, tone = "red", testid }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const parts = draft.split(/[,;\n]+/).map((w) => w.trim().toLowerCase()).filter(Boolean);
    if (!parts.length) return;
    onChange(Array.from(new Set([...(words || []), ...parts])));
    setDraft("");
  };
  const chip = tone === "red" ? "border-red-300 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-950/30 dark:text-red-300" : "border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-300";
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <div><Label>{label}</Label>{hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}</div>
        {(defaults || []).length > 0 && <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={() => onChange([...defaults])} data-testid={`${testid}-reset`}><ArrowCounterClockwise size={12} className="mr-1" /> Reset to defaults</Button>}
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`${testid}-chips`}>
        {(words || []).map((w) => (
          <span key={w} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${chip}`} data-testid={`${testid}-chip-${w.replace(/[^a-z0-9]+/g, "_")}`}>
            {w}
            <button type="button" onClick={() => onChange(words.filter((x) => x !== w))} aria-label={`Remove ${w}`} className="opacity-70 hover:opacity-100" data-testid={`${testid}-remove-${w.replace(/[^a-z0-9]+/g, "_")}`}><X size={10} /></button>
          </span>
        ))}
        {!(words || []).length && <span className="text-xs text-muted-foreground">{(defaults || []).length ? "No words — nothing will be flagged." : "None yet — add a word or phrase below."}</span>}
      </div>
      <div className="mt-2 flex gap-2">
        <Input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} placeholder="Add a word or phrase (comma-separate several)" className="h-8 text-xs" data-testid={`${testid}-input`} />
        <Button type="button" variant="outline" size="sm" className="h-8" onClick={add} disabled={!draft.trim()} data-testid={`${testid}-add`}><Plus size={12} className="mr-1" /> Add</Button>
      </div>
    </div>
  );
}

/** Urgent alert note + urgent / fault word lists. */
export default function InboxUrgencySection({ s, setS, options }) {
  const n = options?.urgent_alert_recipients;
  return (
    <div className="space-y-4" data-testid="inbox-urgency-section">
      <div className="flex items-start gap-3 rounded-md border border-red-200 dark:border-red-900/60 bg-red-50/60 dark:bg-red-950/20 p-3" data-testid="inbox-urgent-alert-note">
        <BellRinging size={18} weight="fill" className="text-red-600 shrink-0 mt-0.5" />
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-red-700 dark:text-red-300">Urgent email alerts</p>
          <p className="text-xs text-muted-foreground">Any email flagged urgent — by a word below or by the AI — sends an immediate push / in-app alert to <strong className="text-foreground">every admin and manager</strong>{typeof n === "number" ? ` (${n} ${n === 1 ? "person" : "people"} right now)` : ""}. Everyone with inbox access still gets the normal new-email notification.</p>
        </div>
      </div>
      <WordsEditor label="Urgent words" hint="Any match in the subject or body flags the email Urgent (priority urgent, red badge, alert above)." words={s.urgent_words} defaults={options?.default_urgent_words || []} onChange={(w) => setS({ ...s, urgent_words: w })} tone="red" testid="inbox-urgent-words" />
      <WordsEditor label="Everyday fault words" hint="Matches flag the email as a fault and raise it to High priority (unless an urgent word also matches)." words={s.fault_words} defaults={options?.default_fault_words || []} onChange={(w) => setS({ ...s, fault_words: w })} tone="amber" testid="inbox-fault-words" />
    </div>
  );
}
